import { T } from '@/lib/theme'
import horizonIcon from '@/assets/tokens/horizon.webp'
import usdxIcon from '@/assets/tokens/usdx.webp'
import ethIcon from '@/assets/tokens/eth.webp'
import husdIcon from '@/assets/tokens/husd.webp'
import shusdIcon from '@/assets/tokens/shusd.webp'
import usdtIcon from '@/assets/tokens/usdt.webp'
import xautIcon from '@/assets/tokens/xaut.webp'
import wbtcIcon from '@/assets/tokens/wbtc.webp'
import wstethIcon from '@/assets/tokens/wsteth.webp'
import x28Icon from '@/assets/tokens/x28.webp'
// Filenames here are inconsistent with each other (`Ouroboros.png` is capitalised,
// `auryn-logo.png` is hyphenated, `titanxlogo.png` runs together) because they arrived as
// supplied artwork. Left as delivered and imported verbatim rather than renamed: the case in
// particular is load-bearing, since the dev filesystem is case-insensitive and the build host is
// not, so a "tidied" import that still resolves locally would 404 only in production.
import usdcIcon from '@/assets/tokens/usdcoin-USDC-logo.webp'
import titanxIcon from '@/assets/tokens/titanxlogo.webp'
import dragonxIcon from '@/assets/tokens/dragonxlogo.webp'
import orxIcon from '@/assets/tokens/Ouroboros.webp'
import aurynIcon from '@/assets/tokens/auryn-logo.webp'

// ─────────────────────────────────────────────────────────────────
// Token glyphs - real artwork for this app's own known tokens (same src/assets/tokens/*.webp
// already used by StakeSection.jsx/MarketsSection.jsx/Footer.jsx), letter-avatar fallback for
// everything else. No fetched/hosted logo for an unverified token - guessing a well-known
// project's logo for an address we haven't confirmed would be worse than an honest initial.
// Fallback color is deterministically derived from the symbol itself (not random, not
// fabricated) so the same token always renders the same color and different tokens read as
// visually distinct in a list.
// ─────────────────────────────────────────────────────────────────

// Every source here is SQUARE on purpose. These render circular (`borderRadius: 50%` with
// `objectFit: cover` below), so a non-square source gets centre-cropped against the FRAME rather
// than its subject - HORIZON's own artwork is 892x787 scene art, and using it raw pushed the moon
// visibly off-centre in every token list until it was cropped square around the moon itself.
const KNOWN_ICONS = {
  HORIZON: horizonIcon,
  USDX: usdxIcon,
  ETH: ethIcon,
  WETH: ethIcon,
  HUSD: husdIcon,   // keys MUST be UPPERCASE — the lookup does symbol.toUpperCase()
  SHUSD: shusdIcon, // shUSD savings token
  // Known tokens with pinned mainnet addresses in config/reserves.json, so real artwork rather
  // than a letter avatar is consistent with this file own rule - it is only an UNVERIFIED address
  // that must not get a guessed logo.
  USDC: usdcIcon,
  USDT: usdtIcon,   // Tether USD
  XAUT: xautIcon,   // Tether Gold (symbol XAUt on chain, uppercased on lookup)
  WBTC: wbtcIcon,
  WSTETH: wstethIcon,
  // TitanX-ecosystem tokens, all with pinned mainnet addresses in scripts/createLaunchVaults.cjs's
  // own TOKENS map and live vault specs in config/launchVaults.json - so the "verified address"
  // rule above is satisfied for each, and a letter avatar would be a downgrade rather than honesty.
  TITANX: titanxIcon,
  DRAGONX: dragonxIcon,
  ORX: orxIcon,
  // X28 - pinned mainnet address in createLaunchVaults.cjs's TOKENS map, same verified-address basis.
  X28: x28Icon,
  // AURYN trades under the ticker "AU" - that is what `symbol()` returns on chain
  // (0x58d7Fa73...), and this table is keyed by ticker, so the project name alone missed and the
  // token fell back to a letter avatar. Both keys are kept: "AU" is what the app actually reads
  // from the token, "AURYN" is what this repo's own config and copy call it
  // (config/launchVaults.json's pair specs, contracts.js's staking blurb), and a future surface
  // labelling it by project name should get the artwork too rather than silently degrading.
  AU: aurynIcon,
  AURYN: aurynIcon,
  // HORIZON (Horizon protocol's token) - supplied artwork, 640x640 square, rendered circular below.
}

const PALETTE = [T.accent, T.cyan, T.purple, T.positive, T.warning]

function colorForSymbol(symbol) {
  const s = symbol || '?'
  let hash = 0
  for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) | 0
  return PALETTE[Math.abs(hash) % PALETTE.length]
}

export function TokenGlyph({ symbol, size = 22 }) {
  const icon = KNOWN_ICONS[(symbol || '').toUpperCase()]
  if (icon) {
    return <img src={icon} alt={symbol} width={size} height={size} decoding="async" style={{ borderRadius: '50%', flexShrink: 0, objectFit: 'cover' }} />
  }
  const initial = (symbol || '?').slice(0, 1).toUpperCase()
  const color = colorForSymbol(symbol)
  return (
    <span style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: color + '22', color, fontFamily: T.fontD, fontWeight: 800, fontSize: size * 0.5,
    }}>{initial}</span>
  )
}

/// Overlapping pair - the second glyph sits behind and offset from the first, ringed in the
/// page's own solid background color so the overlap reads as a clean cutout rather than two
/// circles crossing edge-to-edge. `symbols` is 1 or 2 token symbols.
export function PairGlyph({ symbols, size = 28 }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
      {symbols.map((s, i) => (
        <div key={i} style={{
          marginLeft: i === 0 ? 0 : -Math.round(size * 0.38),
          zIndex: symbols.length - i,
          lineHeight: 0,
          borderRadius: '50%',
          boxShadow: `0 0 0 2px ${T.bg}`,
        }}>
          <TokenGlyph symbol={s} size={size} />
        </div>
      ))}
    </div>
  )
}
